import { useLingui } from '@lingui/react/macro';
import {
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from '@repo/videa-components/src/ui/hover-card';

import { StatusChip } from '@/common-components/elements/StatusChip/StatusChip';
import { useReferralStatus } from '@/react-query/useReferralInfo';

interface ReferralStatusPillProps {
  unifiedAppointmentId?: string;
}

export const ReferralStatusPill = ({
  unifiedAppointmentId,
}: ReferralStatusPillProps) => {
  const { t } = useLingui();

  const { data: referralStatusData } = useReferralStatus(
    unifiedAppointmentId ?? '',
    {
      enabled: !!unifiedAppointmentId,
    },
  );
  const referralStatus = referralStatusData?.status;

  if (!unifiedAppointmentId) {
    return null;
  }

  if (
    !referralStatus ||
    (referralStatus !== 'submitted' &&
      referralStatus !== 'needs_referral' &&
      referralStatus !== 'failed')
  ) {
    return null;
  }

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'submitted':
        return {
          label: t`Referral Submitted`,
          description: t`Referral has been submitted successfully`,
          variant: 'success' as const,
        };
      case 'needs_referral':
        return {
          label: t`Needs Referral`,
          description: t`Referral has been flagged for attention`,
          variant: 'warning' as const,
        };
      case 'failed':
        return {
          label: t`Needs Resubmission`,
          description: t`Referral failed to send, and needs resubmission`,
          variant: 'error' as const,
        };
      default:
        return {
          label: t`Unknown Status`,
          description: t`Unknown referral status`,
          variant: 'error' as const,
        };
    }
  };

  const { label, description, variant } = getStatusConfig(referralStatus);

  return (
    <HoverCard openDelay={100} closeDelay={100}>
      <HoverCardTrigger className="vai-self-start">
        <StatusChip
          text={label}
          variant={variant}
          className="vai-rounded-3xl vai-py-1 vai-px-2"
        />
      </HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent className="vai-w-64">
          <p className="vai-typography-p">{description}</p>
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
};
